<!--
  Derived authentication page using as a UI bridge for SSO renewal

  Example:
    - user is navigated on "documentation.ghs.fr"
    - a cookie is set on "ghs.fr" (server side)
    - session needs renewal
    - user is redirected here "hamac.ghs.fr" (while a desired URL is provided
      in query params)
    - UI signin renewal
    - a new cookie is set on "ghs.fr" (server side)
    - redirection to the "documentation.ghs.fr" is made

-->
<template>
  <div
    ref="container"
    class="page-auth"
    data-cy="page:dispatch-login"
  >
    <ForumParagraph
      v-if="isForumBlockDisplayed"
      class="page-auth__forum"
    />

    <transition
      v-if="isOnScreen(SCREEN.LOGIN)"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <auth-login
        :key="SCREEN.LOGIN"
        class="page-auth__form"
        :is-pending="isPending"
        :callback="loginCallback"
        :has-remote-error="hasRemoteError"
        :login-label="loginLabel"
      />
    </transition>

    <transition
      v-if="isOnScreen(SCREEN.MFA)"
      appear
      appear-active-class="animate__animated animate__fadeIn ease-in-out a3"
    >
      <mfa-form
        :key="SCREEN.MFA"
        ref="mfaForm"
        :is-pending="isPending"
        :submit-callback="onSubmitMfa"
        :has-remote-error="hasRemoteError"
        :remote-error-message="remoteErrorMessage"
        focus
      />
    </transition>
  </div>
</template>

<style lang="sass" scoped>

.page-auth

  &__forum
    $extrude-top: - space-x(2)

    margin-top: $extrude-top
    margin-bottom: space-x(6)

/// Animation
///
/// On login success, animate the whole page as if we were transporting
/// within a cloud
.page-auth--success
  transition: opacity .4s ease-in-out
  opacity: 0

/// Animation
///
/// On login error, animate the error caption (handy when the authentication
/// failed twice and thus give a user feedback of the error)
.page-auth
  :deep(.login-form__error)
    visibility: hidden
.page-auth.page-auth--error
  :deep(.login-form__error)
    visibility: visible
    animation: headShake 1s ease-in-out 0.2s, fade-in 0.4s ease-in-out

</style>

<script lang="ts">
import AuthLogin from '~/components/account/authentication/LoginFormBis.vue'
import Console from '~/utils/lib/core/console'
import { assoc, delay, equals, not, propOr, sustainedPromise, test, toString } from '~/utils/lib/core/prelude'
import { MICRO_SERVICE_ERRORS as ACCOUNT_MICRO_SERVICE_ERRORS, observer as accountObserver, EVENTS as ACCOUNT_EVENTS } from '~/utils/infra/account/types'
import AccountAPIError from '~/utils/infra/account/error'
import { cause } from '~/utils/infra/common/helpers'
import MfaForm from '~/components/account/authentication/MfaForm.vue'
import { FALLBACK_DISPATCH_URL, TRY_LOGIN_SUCCESS_TYPE, useStore_account_authentication } from '~/stores/account/authentication'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useStore_account_session } from '~/stores/account/session'
import type { RouteLocationGeneric } from 'vue-router'
import ForumParagraph from '~/components/account/authentication/ForumParagraph.vue'

const console = new Console('component:dispatch')

type SCREEN = typeof SCREEN[keyof typeof SCREEN]
const SCREEN = {
  LOGIN: 'login',
  MFA: 'mfa'
} as const

export default defineNuxtComponent({
  components: { AuthLogin, MfaForm, ForumParagraph },
  setup() {
    definePageMeta({
      layout: 'authentication',
      pageTransition: {
        enterActiveClass: 'animate__animated animate__fadeIn ease-in-out a3',
        leaveActiveClass: 'd-none',
      },
      middleware: [
        /// @NOTE #SP-5958: willingfuly not redirecting authenticated user
        ///   ↳ cause: combinaton of two factors
        ///       a) two source of truth for session → a global domain COOKIE
        ///          set by the micro service **plus** a LocalStorage used by
        ///          the application
        ///       b) docutheque refresh policy is a black box (we do not know
        ///          when the "need refresh" will be triggered, nor if the
        ///          global domain COOKIE is altered)
        ///       => hence the docutheque refresh policy can be triggered with
        ///          a redirection to the application even if the LocalStorage
        ///          still bears a working session
        ///   ↳ workaround: disabled the "anonymous-control-gate"
        // 'anonymous-control-gate',
        /**
         * @desc
         *    - Redirect currently authenticated user to the provided
         *      external URL
         *    - Defensive: Make an account refresh if the global domain COOKIE
         *      is removed (application wise, we do not have this info, so just
         *      make a systematic exec)
         */
        async function(to) {
          const authentication = useStore_account_authentication()
          const console = new Console('middleware:dispatch')
          const hasSession = useStore_account_session().has

          if (not(hasSession))
            return

          console.log(
            `catched authenticated user,`,
            `will be redirected accordingly (with the desired URL)`
          )

          return authentication.refresh()
            .then(async () => {
              const url = getDispatchURL(to)

              return navigateTo(url, { external: true })
            })
            .catch(err => {
              console.warn(
                `error when refreshing the current session (while redirecting`,
                `the user on the desired dispatch URL)`,
                err
              )
            })
        }
      ]
    })
    useHead({
      title: 'Connexion — Hamac',
    })

    const refContainer = useTemplateRef<HTMLDivElement>('container')

    const store_authentication = useStore_account_authentication()

    return {
      ...useLinkHandler(),
      refContainer,
      store_authentication,
    }
  },
  data() {
    return {
      SCREEN,
      isPending: false,
      hasRemoteError: false,
      loginLabel: '', // lazy
      remoteErrorMessage: '', // lazy
      isForumBlockDisplayed: false, // lazy
      currentScreen: SCREEN.LOGIN as SCREEN,
    }
  },
  created() {
    this.loginLabel = getLoginLabel(
      this.$route
    )

    this.isForumBlockDisplayed = fromForumContext_(
      this.$route
    )
  },
  methods: {
    onSubmitMfa(code) {
      this.isPending = true
      this.hasRemoteError = false

      // @XXX micro-service only setting domain cookie when the "rememberMe"
      //      feature is set to true
      //        ↳ patch surgery: even if we DO NOT need a remembered session,
      //          we have to set it accordingly
      const rememberMe = true

      return sustainedPromise(
          500, // Delay to prevent flashy animations
          this.store_authentication.submitCode(
            {
              code,
              rememberMe,
            }
          ),
        )
        .then(() => this.onLoginSuccess())
        .catch(err => this.onLoginError(err))
    },
    loginCallback(data) {
      this.isPending = true

      this.refContainer
        ?.classList
        .remove('page-auth--error')

      // @XXX micro-service only setting domain cookie when the "rememberMe"
      //      feature is set to true
      //        ↳ patch surgery: even if we DO NOT need a remembered session,
      //          we have to set it accordingly
      const data_ = assoc('rememberMe', true, data)

      return this.store_authentication.login_v2(data_)
        .then(res => {
          switch (res) {

            case TRY_LOGIN_SUCCESS_TYPE.MFA_CHALLENGE:
              this.hasRemoteError = false
              this.currentScreen = SCREEN.MFA
              this.isPending = false
              break

            case TRY_LOGIN_SUCCESS_TYPE.COMPLETE:
              return this.onLoginSuccess()
          }
        })
        .catch(err => this.onLoginError(err))
    },
    onLoginError(err) {

      switch (true) {

        case
          (  err instanceof AccountAPIError
          && cause(err) === ACCOUNT_MICRO_SERVICE_ERRORS.LOGIN_INCORRECT_PASSWORD
          ):
        case
          (  err instanceof AccountAPIError
          && cause(err) === ACCOUNT_MICRO_SERVICE_ERRORS.LOGIN_ACCOUNT_NOT_FOUND
          ):
            // as we are dealing a signup form, this must be an invalid
            // credentials → silently discard the error
            break

        default:

          console.warn('login error', toString(err), err)

          break
      }

      this.hasRemoteError = true
      this.remoteErrorMessage = toString(err)
      this.isPending = false

      // animate server error caption
      this.refContainer
        ?.classList
        .add('page-auth--error')
    },
    async onLoginSuccess() {

      this.refContainer
        ?.classList
        .add('page-auth--success')

      window.document.querySelector('.layout-authentication')
        ?.classList
        .add('layout-authentication--success')

      // wait for animation to finish off
      await delay(400)

      const url = getDispatchURL(this.$route)

      // @XXX micro-service only setting domain cookie when the "rememberMe"
      //      feature is set to true
      //    ↳ patch surgery: as we had previously set the "rememberMe" to
      //      true above, we will also perform the whole signin process
      return accountObserver.emit(ACCOUNT_EVENTS.ON_DISPATCH_SIGNIN, url)
    },
    isOnScreen(screen :SCREEN) {
      return equals(screen, this.currentScreen)
    }
  },
})

const fromForumContext = (url :string) => test(/forum/, url)
const fromForumContext_ = (route :RouteLocationGeneric) => fromForumContext(
  (route.query.url ?? '') as string
)
/**
 * @function getDispatchURL
 * @static
 */
function getDispatchURL(route :RouteLocationGeneric) :string {
  return propOr(FALLBACK_DISPATCH_URL, 'url', route.query)
}
/**
 * @function getLoginLabel
 * @static
 */
function getLoginLabel(route :RouteLocationGeneric) :string {
  const url = (route.query.url ?? '') as string

  switch (true) {

    case fromForumContext(url):
      return `Email`

    default:
      return `Code client GHS`
  }
}
</script>
